/**
 * Atlas — Reusable UI primitives matching the design system.
 */
import React from 'react';

// ── Grade Pill ──
export function GradePill({ grade, size = 'md' }) {
  const g = grade.charAt(0);
  const colors = {
    A: { bg: '#1f3a2e', fg: '#f5f1e8' },
    B: { bg: '#5d7a5e', fg: '#f5f1e8' },
    C: { bg: '#a87a2a', fg: '#f5f1e8' },
    D: { bg: '#8b2e2e', fg: '#f5f1e8' },
  };
  const c = colors[g] || colors.B;
  const s = size === 'lg' ? 56 : size === 'sm' ? 24 : 36;
  const fs = size === 'lg' ? 22 : size === 'sm' ? 11 : 15;

  return (
    <div style={{
      width: s, height: s, borderRadius: '50%',
      background: c.bg, color: c.fg,
      display: 'grid', placeItems: 'center',
      fontFamily: 'var(--serif)', fontWeight: 600,
      fontSize: fs, letterSpacing: '-0.02em', flexShrink: 0,
    }}>
      {grade}
    </div>
  );
}

// ── Verified Badge ──
export function Verified({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2l2.5 2 3.5-.5.5 3.5L21 9l-2 3 2 3-2.5 2-.5 3.5L14.5 20 12 22l-2.5-2-3.5.5-.5-3.5L3 15l2-3-2-3 2.5-2 .5-3.5L9.5 4 12 2Z"
        fill="#1f3a2e" stroke="#1f3a2e" strokeWidth="1" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="#f5f1e8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

// ── Avatar ──
export function Avatar({ initials, color = '#1f3a2e', size = 32 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#f5f1e8',
      display: 'grid', placeItems: 'center',
      fontSize: size * 0.36, fontWeight: 600,
      fontFamily: 'var(--sans)', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// ── Chip ──
export function Chip({ children, variant = '', onClick, active, style = {} }) {
  return (
    <span
      className={`chip ${variant} ${active ? 'active' : ''}`}
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        ...(active ? {
          background: 'var(--forest)', color: 'var(--paper)',
          borderColor: 'var(--forest)',
        } : {}),
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ── Panel ──
export function Panel({ children, className = '', style = {} }) {
  return (
    <div className={`panel ${className}`} style={style}>
      {children}
    </div>
  );
}

export function PanelHead({ title, meta, children }) {
  return (
    <div className="panel-head">
      <h3>{title}</h3>
      {meta && <span className="meta">{meta}</span>}
      {children}
    </div>
  );
}

// ── Loading Spinner ──
export function Spinner({ size = 24 }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'center',
      alignItems: 'center', padding: 40,
    }}>
      <div style={{
        width: size, height: size,
        border: '2px solid var(--hairline)',
        borderTopColor: 'var(--forest)',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Empty State ──
export function EmptyState({ message = 'Nothing here yet', icon }) {
  return (
    <div style={{
      textAlign: 'center', padding: 60,
      color: 'var(--ink-4)', fontSize: 14,
    }}>
      {icon && <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>{icon}</div>}
      <div>{message}</div>
    </div>
  );
}

// ── Format Helpers ──
export const fmtCost = (n) => {
  if (n == null) return '—';
  return '$' + n.toLocaleString();
};

export const fmtDate = (iso, opts = {}) => {
  const d = new Date(iso);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (opts.short) return `${m[d.getMonth()]} ${d.getFullYear()}`;
  return `${m[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

export const TYPE_META = {
  service:    { label: 'Service',    color: '#5d7a5e', chip: 'moss' },
  renovation: { label: 'Renovation', color: '#a87a2a', chip: 'amber' },
  inspection: { label: 'Inspection', color: '#1f3a2e', chip: 'forest' },
  compliance: { label: 'Compliance', color: '#8b2e2e', chip: 'crimson' },
  ownership:  { label: 'Ownership',  color: '#1a1f1c', chip: '' },
};
